import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button, Card, Grid, Tooltip, Typography } from "@mui/material";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { serviceProvider } from "../../provider/serviceProvider";
import { getFormattedDate } from "../../utility/formateDateAndTime";
import DatePickerComponent from "../common/DatePickerComponent";
import ButtonComponent from "../common/ButtonComponent";
import DataGridTable from "../DataGridTable/DataGridTable";
import CommonModal from "../modal/CommonModal";
import SpinLoader from "../loader/SpinLoader";
import viewIcon from "../../assets/icons/view-query.svg";

type QueryType = "General" | "Empty Leg" | "Quotation" | "BMJ Exclusive";

type NormalizedQuery = {
  id: string;
  name: string;
  email: string;
  contact: string;
  recordCreatedTs: string;
  queryType: QueryType;
  status: string;
  rawData: any;
};

type SummaryCounts = {
  general: number;
  emptyLeg: number;
  quotation: number;
  bmjExclusive: number;
};

const QUERY_TYPES: {
  label: QueryType;
  queryType: string;
  borderColor: string;
}[] = [
  { label: "General", queryType: "GENERAL", borderColor: "#FFF8DD" },
  { label: "Empty Leg", queryType: "EMPTYLEG", borderColor: "#F0F9FE" },
  { label: "Quotation", queryType: "QUOTATION", borderColor: "#FEF4F8" },
  { label: "BMJ Exclusive", queryType: "BMJEXCLUSIVE", borderColor: "#E7FFF3" },
];

const formatApiDate = (date: any) =>
  date
    ? dayjs(new Date(date).toISOString()).format("YYYY-MM-DDTHH:mm:ss.SSSZ")
    : "all";

const normalizeGeneralQuery = (query: any): NormalizedQuery => ({
  id: `general-${query.queryId}`,
  name: query.fullName || "--",
  email: query.email || "--",
  contact: query.contact || "--",
  recordCreatedTs: query.recordCreatedTs,
  queryType: "General",
  status: query.status || "--",
  rawData: query,
});

const normalizeQuoteQuery = (
  query: any,
  queryType: "Quotation" | "BMJ Exclusive",
): NormalizedQuery => ({
  id: `${queryType.toLowerCase().replace(" ", "-")}-${query.quoteEnquiryId || query.enquiryId || query.queryId}`,
  name: query.name || "--",
  email: query.email || "--",
  contact: query.mobileNumber || "--",
  recordCreatedTs: query.recordCreatedTs,
  queryType,
  status: query.status || "--",
  rawData: query,
});

const normalizeEmptyLegQuery = (query: any): NormalizedQuery => ({
  id: `empty-leg-${query.emptyLegEnquiryId}`,
  name: query.name || "--",
  email: query.email || "--",
  contact: query.mobileNumber || "--",
  recordCreatedTs: query.recordCreatedTs,
  queryType: "Empty Leg",
  status: query.status || "--",
  rawData: query,
});

const sortByDateDesc = (queries: NormalizedQuery[]) =>
  [...queries].sort(
    (a, b) =>
      new Date(b.recordCreatedTs).getTime() -
      new Date(a.recordCreatedTs).getTime(),
  );

const buildCommonQueryParam = (
  queryType: string,
  status: string,
  fromDate: any,
  toDate: any,
  offset = 0,
  limit = 100,
) =>
  `allqueries/search?status=${status}&queryType=${queryType}&from=${formatApiDate(
    fromDate,
  )}&to=${formatApiDate(toDate)}&offset=${offset}&limit=${limit}&order=updatedTs.desc&profile=BASIC`;

const fetchCommonQueriesByStatus = async (
  queryType: string,
  status: string,
  fromDate: any,
  toDate: any,
  offset = 0,
  limit = 100,
) => {
  const param = buildCommonQueryParam(
    queryType,
    status,
    fromDate,
    toDate,
    offset,
    limit,
  );
  const res: any = await serviceProvider.commonService.get(param);
  if (res.responseCode !== 200) {
    throw new Error(res?.message || "Failed to fetch queries");
  }
  return {
    data: res.data?.data || [],
    totalRecordCount: res.data?.totalRecordCount || 0,
  };
};

const fetchCommonQueries = async (
  queryType: string,
  fromDate: any,
  toDate: any,
  offset = 0,
  limit = 100,
) => {
  const [pending, resolved] = await Promise.all([
    fetchCommonQueriesByStatus(
      queryType,
      "PENDING",
      fromDate,
      toDate,
      offset,
      limit,
    ),
    fetchCommonQueriesByStatus(
      queryType,
      "RESOLVED",
      fromDate,
      toDate,
      offset,
      limit,
    ),
  ]);

  return {
    data: [...pending.data, ...resolved.data],
    totalRecordCount: pending.totalRecordCount + resolved.totalRecordCount,
  };
};

const Dashboard: React.FC = () => {
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState("");
  const [summaryCounts, setSummaryCounts] = useState<SummaryCounts>({
    general: 0,
    emptyLeg: 0,
    quotation: 0,
    bmjExclusive: 0,
  });
  const [latestQueries, setLatestQueries] = useState<NormalizedQuery[]>([]);
  const [queryListData, setQueryListData] = useState<NormalizedQuery[]>([]);

  const [startDate, setStartDate] = useState<any>(null);
  const [endDate, setEndDate] = useState<any>(null);
  const [appliedStartDate, setAppliedStartDate] = useState<any>(null);
  const [appliedEndDate, setAppliedEndDate] = useState<any>(null);

  const [latestPageState, setLatestPageState] = useState({
    loader: false,
    page: 0,
    pageSize: 5,
    totalPages: 0,
  });
  const [queryListPageState, setQueryListPageState] = useState({
    loader: false,
    page: 0,
    pageSize: 5,
    totalPages: 0,
  });

  const [open, setOpen] = useState(false);
  const [selectedData, setSelectedData] = useState<NormalizedQuery | null>(
    null,
  );

  const handleOpenViewModal = (rowData: NormalizedQuery) => {
    setSelectedData(rowData);
    setOpen(true);
  };

  const handleCloseModal = () => {
    setOpen(false);
    setSelectedData(null);
  };

  const fetchEmptyLegQueries = async (offset = 0, limit = 100) => {
    const param = `enquiry/search?email=&offset=${offset}&limit=${limit}&order=recordCreatedTs.desc`;
    const res: any = await serviceProvider.emptyLegService.get(param);
    if (res.responseCode !== 200) {
      throw new Error(res?.message || "Failed to fetch empty leg queries");
    }
    return {
      data: res.data?.data || [],
      totalRecordCount: res.data?.totalRecordCount || 0,
    };
  };

  const fetchSummaryCounts = useCallback(async () => {
    const [general, quotation, bmjExclusive, emptyLeg] = await Promise.all([
      fetchCommonQueries("GENERAL", null, null, 0, 1),
      fetchCommonQueries("QUOTATION", null, null, 0, 1),
      fetchCommonQueries("BMJEXCLUSIVE", null, null, 0, 1),
      fetchEmptyLegQueries(0, 1),
    ]);

    setSummaryCounts({
      general: general.totalRecordCount,
      emptyLeg: emptyLeg.totalRecordCount,
      quotation: quotation.totalRecordCount,
      bmjExclusive: bmjExclusive.totalRecordCount,
    });
  }, []);

  const fetchLatestQueries = useCallback(async () => {
    setLatestPageState((prev) => ({ ...prev, loader: true }));

    const [general, quotation, bmjExclusive, emptyLeg] = await Promise.all([
      // fetchCommonQueries("GENERAL", null, null, 0, 5),
      // fetchCommonQueries("QUOTATION", null, null, 0, 5),
      // fetchCommonQueries("BMJEXCLUSIVE", null, null, 0, 5),
      // fetchEmptyLegQueries(0, 5),
      fetchCommonQueries("GENERAL", null, null, 0, 100),
      fetchCommonQueries("QUOTATION", null, null, 0, 100),
      fetchCommonQueries("BMJEXCLUSIVE", null, null, 0, 100),
      fetchEmptyLegQueries(0, 100),
    ]);

    const sevenDaysAgo = dayjs().subtract(7, "day").startOf("day");

    const merged = sortByDateDesc([
      ...general.data.map(normalizeGeneralQuery),
      ...quotation.data.map((item: any) =>
        normalizeQuoteQuery(item, "Quotation"),
      ),
      ...bmjExclusive.data.map((item: any) =>
        normalizeQuoteQuery(item, "BMJ Exclusive"),
      ),
      ...emptyLeg.data.map(normalizeEmptyLegQuery),
    ])
      .filter((item) => dayjs(item.recordCreatedTs).isAfter(sevenDaysAgo))
      .slice(0, 5);

    setLatestQueries(merged);
    setLatestPageState((prev) => ({
      ...prev,
      loader: false,
      totalPages: merged.length,
    }));
  }, []);

  const fetchQueryList = useCallback(async (fromDate: any, toDate: any) => {
    setQueryListPageState((prev) => ({ ...prev, loader: true, page: 0 }));

    const [general, quotation, bmjExclusive, emptyLeg] = await Promise.all([
      fetchCommonQueries("GENERAL", fromDate, toDate),
      fetchCommonQueries("QUOTATION", fromDate, toDate),
      fetchCommonQueries("BMJEXCLUSIVE", fromDate, toDate),
      fetchEmptyLegQueries(),
    ]);

    let merged = sortByDateDesc([
      ...general.data.map(normalizeGeneralQuery),
      ...quotation.data.map((item: any) =>
        normalizeQuoteQuery(item, "Quotation"),
      ),
      ...bmjExclusive.data.map((item: any) =>
        normalizeQuoteQuery(item, "BMJ Exclusive"),
      ),
      ...emptyLeg.data.map(normalizeEmptyLegQuery),
    ]);

    if (fromDate || toDate) {
      merged = merged.filter((query) => {
        const queryDate = new Date(query.recordCreatedTs).getTime();
        const from = fromDate ? dayjs(fromDate).startOf("day").valueOf() : null;
        const to = toDate ? dayjs(toDate).endOf("day").valueOf() : null;

        if (from !== null && queryDate < from) return false;
        if (to !== null && queryDate > to) return false;
        return true;
      });
    }

    setQueryListData(merged);
    setQueryListPageState((prev) => ({
      ...prev,
      loader: false,
      page: 0,
      totalPages: merged.length,
    }));
  }, []);

  const loadDashboard = useCallback(async () => {
    setInitialLoading(true);
    setError("");
    try {
      await Promise.all([
        fetchSummaryCounts(),
        fetchLatestQueries(),
        fetchQueryList(null, null),
      ]);
    } catch (err: any) {
      const message =
        err?.data?.message || err?.message || "Failed to load dashboard data";
      setError(message);
      toast.error(message);
    } finally {
      setInitialLoading(false);
    }
  }, [fetchLatestQueries, fetchQueryList, fetchSummaryCounts]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const handleSearch = async () => {
    setAppliedStartDate(startDate);
    setAppliedEndDate(endDate);
    setError("");
    try {
      await fetchQueryList(startDate, endDate);
    } catch (err: any) {
      const message =
        err?.data?.message || err?.message || "Failed to search queries";
      setError(message);
      toast.error(message);
    }
  };

  const handleReset = async () => {
    setStartDate(null);
    setEndDate(null);
    setAppliedStartDate(null);
    setAppliedEndDate(null);
    setError("");
    try {
      await fetchQueryList(null, null);
    } catch (err: any) {
      const message =
        err?.data?.message || err?.message || "Failed to reset filters";
      setError(message);
      toast.error(message);
    }
  };

  const latestRows = useMemo(
    () =>
      latestQueries.map((query) => ({
        ...query,
        date: getFormattedDate(query.recordCreatedTs),
        type: query.queryType,
      })),
    [latestQueries],
  );

  const paginatedQueryListRows = useMemo(() => {
    const start = queryListPageState.page * queryListPageState.pageSize;
    const end = start + queryListPageState.pageSize;
    return queryListData.slice(start, end).map((query) => ({
      ...query,
      createdDate: getFormattedDate(query.recordCreatedTs),
    }));
  }, [queryListData, queryListPageState.page, queryListPageState.pageSize]);

  const latestColumns = [
    {
      field: "name",
      headerName: "Name",
      flex: 1,
      minWidth: 140,
      renderCell: (params: any) => (
        <Tooltip title={params.row?.name}>
          <span>{params.row?.name}</span>
        </Tooltip>
      ),
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1,
      minWidth: 180,
      renderCell: (params: any) => (
        <Tooltip title={params.row?.email}>
          <span>{params.row?.email}</span>
        </Tooltip>
      ),
    },
    {
      field: "contact",
      headerName: "Contact",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "date",
      headerName: "Date",
      flex: 1,
      minWidth: 130,
    },
    {
      field: "type",
      headerName: "Type",
      flex: 1,
      minWidth: 130,
    },
    {
      field: "viewDetails",
      type: "actions",
      headerName: "View Details",
      width: 120,
      getActions: (params: any) => [
        <Button key="view" onClick={() => handleOpenViewModal(params.row)}>
          <img src={viewIcon} alt="view icon" height="20px" width="20px" />
        </Button>,
      ],
    },
  ];

  const queryListColumns = [
    {
      field: "name",
      headerName: "Name",
      flex: 1,
      minWidth: 140,
      renderCell: (params: any) => (
        <Tooltip title={params.row?.name}>
          <span>{params.row?.name}</span>
        </Tooltip>
      ),
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1,
      minWidth: 180,
      renderCell: (params: any) => (
        <Tooltip title={params.row?.email}>
          <span>{params.row?.email}</span>
        </Tooltip>
      ),
    },
    {
      field: "contact",
      headerName: "Contact",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "queryType",
      headerName: "Query Type",
      flex: 1,
      minWidth: 130,
    },
    {
      field: "createdDate",
      headerName: "Created Date",
      flex: 1,
      minWidth: 130,
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      minWidth: 110,
    },
    {
      field: "viewDetails",
      type: "actions",
      headerName: "View Details",
      width: 120,
      getActions: (params: any) => [
        <Button key="view" onClick={() => handleOpenViewModal(params.row)}>
          <img src={viewIcon} alt="view icon" height="20px" width="20px" />
        </Button>,
      ],
    },
  ];

  const summaryCardValues = [
    summaryCounts.general,
    summaryCounts.emptyLeg,
    summaryCounts.quotation,
    summaryCounts.bmjExclusive,
  ];

  const renderDetailsModalBody = () => {
    if (!selectedData) {
      return <Typography>Loading...</Typography>;
    }

    const { rawData, queryType } = selectedData;

    return (
      <Grid container>
        <Grid item xs={12} borderBottom="1px solid #CA3657" mb={1} pb={1}>
          <Typography variant="body1" fontWeight="bold" color="#CA3657">
            Query Type:{" "}
            <Typography component="span" fontWeight="bold" color="black">
              {queryType}
            </Typography>
          </Typography>
        </Grid>

        <Grid
          container
          item
          xs={12}
          mb={1}
          pb={1}
          display="flex"
          alignItems="center"
        >
          <Typography variant="body1" fontWeight="700" mr={1}>
            Name:
          </Typography>
          <Typography variant="body1">{selectedData.name}</Typography>
        </Grid>

        <Grid
          container
          item
          xs={12}
          mb={1}
          pb={1}
          display="flex"
          alignItems="center"
        >
          <Typography variant="body1" fontWeight="700" mr={1}>
            Email:
          </Typography>
          <Typography variant="body1">{selectedData.email}</Typography>
        </Grid>

        <Grid
          container
          item
          xs={12}
          mb={1}
          pb={1}
          display="flex"
          alignItems="center"
        >
          <Typography variant="body1" fontWeight="700" mr={1}>
            Contact:
          </Typography>
          <Typography variant="body1">{selectedData.contact}</Typography>
        </Grid>

        <Grid
          container
          item
          xs={12}
          mb={1}
          pb={1}
          display="flex"
          alignItems="center"
        >
          <Typography variant="body1" fontWeight="700" mr={1}>
            Status:
          </Typography>
          <Typography variant="body1">{selectedData.status}</Typography>
        </Grid>

        <Grid
          container
          item
          xs={12}
          mb={1}
          pb={1}
          display="flex"
          alignItems="center"
        >
          <Typography variant="body1" fontWeight="700" mr={1}>
            Created Date:
          </Typography>
          <Typography variant="body1">
            {getFormattedDate(selectedData.recordCreatedTs)}
          </Typography>
        </Grid>

        {queryType === "General" && (
          <Grid
            container
            item
            xs={12}
            mb={1}
            pb={1}
            display="flex"
            alignItems="center"
          >
            <Typography variant="body1" fontWeight="700" mr={1}>
              Query:
            </Typography>
            <Typography variant="body1">
              {rawData.queryDescription || "--"}
            </Typography>
          </Grid>
        )}

        {(queryType === "Quotation" || queryType === "BMJ Exclusive") && (
          <>
            <Grid
              container
              item
              xs={12}
              mb={1}
              pb={1}
              display="flex"
              alignItems="center"
            >
              <Typography variant="body1" fontWeight="700" mr={1}>
                Query:
              </Typography>
              <Typography variant="body1">
                {rawData.otherDetails || "--"}
              </Typography>
            </Grid>
            {rawData.itineraryDetails?.itineraryType && (
              <Grid
                container
                item
                xs={12}
                mb={1}
                pb={1}
                display="flex"
                alignItems="center"
              >
                <Typography variant="body1" fontWeight="700" mr={1}>
                  Itinerary Type:
                </Typography>
                <Typography variant="body1">
                  {rawData.itineraryDetails.itineraryType}
                </Typography>
              </Grid>
            )}
          </>
        )}

        {queryType === "Empty Leg" && rawData.route && (
          <Grid
            container
            item
            xs={12}
            mb={1}
            pb={1}
            display="flex"
            alignItems="center"
          >
            <Typography variant="body1" fontWeight="700" mr={1}>
              Route:
            </Typography>
            <Typography variant="body1">{rawData.route}</Typography>
          </Grid>
        )}
      </Grid>
    );
  };

  if (initialLoading) {
    return <SpinLoader />;
  }

  return (
    <>
      <CommonModal
        open={open}
        handleCloseModal={handleCloseModal}
        modalBody={renderDetailsModalBody()}
      />

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="h6" component="h6">
            Dashboard
          </Typography>
        </Grid>

        {error && (
          <Grid item xs={12}>
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                border: "1px solid #f44336",
                backgroundColor: "#ffebee",
              }}
            >
              <Typography color="error" mb={1}>
                {error}
              </Typography>
              <ButtonComponent
                label="Retry"
                fullWidth={false}
                onClick={loadDashboard}
              />
            </Box>
          </Grid>
        )}

        <Grid item xs={12}>
          <Card sx={{ p: { xs: 1, md: 2 } }}>
            <Grid container spacing={2}>
              {QUERY_TYPES.map((item, index) => (
                <Grid item xs={12} sm={6} lg={3} key={item.label}>
                  <Box
                    sx={{
                      padding: 2,
                      borderRadius: 3,
                      border: `4px solid ${item.borderColor}`,
                      display: "flex",
                      justifyContent: "center",
                      textAlign: "center",
                    }}
                  >
                    <Typography variant="body1" component="div">
                      <b style={{ fontSize: "25px" }}>
                        {summaryCardValues[index]}
                      </b>
                      &nbsp;&nbsp;
                      {`${item.label} Queries`}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="h6" component="h6">
            Latest Queries (Last 7 Days)
          </Typography>
        </Grid>

        <Grid item xs={12}>
          {!latestPageState.loader && latestRows.length === 0 ? (
            <Card sx={{ p: 3, textAlign: "center" }}>
              <Typography color="text.secondary">No queries found.</Typography>
            </Card>
          ) : (
            <DataGridTable
              pageState={latestPageState}
              setPageState={setLatestPageState}
              rows={latestRows}
              columns={latestColumns}
              showToolbar={false}
            />
          )}
        </Grid>

        <Grid item xs={12}>
          <Typography variant="h6" component="h6">
            Query List
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Card sx={{ p: { xs: 1, md: 2 } }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={3}>
                <DatePickerComponent
                  className="date-input"
                  value={startDate}
                  placeholder="Start Date"
                  onChange={(date: any) => setStartDate(date)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <DatePickerComponent
                  className="date-input"
                  value={endDate}
                  placeholder="End Date"
                  onChange={(date: any) => setEndDate(date)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <ButtonComponent
                  label="Search"
                  fullWidth
                  onClick={handleSearch}
                  loader={queryListPageState.loader}
                  disabled={queryListPageState.loader}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <ButtonComponent
                  label="Reset"
                  fullWidth
                  className="rounded-btn-outlined"
                  variant="outlined"
                  onClick={handleReset}
                  disabled={queryListPageState.loader}
                />
              </Grid>
            </Grid>
            {(appliedStartDate || appliedEndDate) && (
              <Typography variant="body2" color="text.secondary" mt={2}>
                Showing results
                {appliedStartDate
                  ? ` from ${getFormattedDate(appliedStartDate)}`
                  : ""}
                {appliedEndDate
                  ? ` to ${getFormattedDate(appliedEndDate)}`
                  : ""}
              </Typography>
            )}
          </Card>
        </Grid>

        <Grid item xs={12}>
          {!queryListPageState.loader && queryListData.length === 0 ? (
            <Card sx={{ p: 3, textAlign: "center" }}>
              <Typography color="text.secondary">
                No queries found for the selected filters.
              </Typography>
            </Card>
          ) : (
            <DataGridTable
              pageState={{
                ...queryListPageState,
                totalPages: queryListData.length,
              }}
              setPageState={setQueryListPageState}
              rows={paginatedQueryListRows}
              columns={queryListColumns}
            />
          )}
        </Grid>
      </Grid>
    </>
  );
};

export default Dashboard;
